import { useHistory } from "react-router";
import { useEffect, useState } from "react";
import CancelButton from "../common/components/CancelButton";
import Div from "../common/components/div";
import H1 from "../common/components/h1";
import PasswordSection from "../common/components/PasswordSection";
import { SmartErrorAlert } from "../common/components/SmartErrorAlert";
import StackingButtons from "../common/components/StackingButtons";
import translate from "../common/functions/translate";
import { SubmitButton } from "./buttons/Buttons";
import { SecurityCodeMethod, useMfaApi } from "./hooks/useMfaApi";
import { useMfaPageView } from '../common/hooks/use.analytics';
import { redirectTo } from "../common/functions/redirectTo";

interface VerifyPasswordProps {
    type: SecurityCodeMethod | null
    flow: 'add' | 'edit' | 'enroll'
    onVerified: (event: React.FormEvent<HTMLFormElement>) => Promise<void> | void
}

export const VerifyPassword = ({ type, flow, onVerified }: VerifyPasswordProps) => {
    const [passwordError, setPasswordError] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [attempts, setAttempts] = useState(0);
    const [shouldValidate, setShouldValidate] = useState(false);
    const [smartErrorKey, setSmartErrorKey] = useState('');

    useMfaPageView(flow, 'enter password');
    const history = useHistory();
    const api = useMfaApi();

    useEffect(() => {
        if (password !== '') {
            setPasswordError('');
            return;
        }
        setPasswordError(translate('enter_your_password'));
    }, [password]);

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setShouldValidate(true);

        if (passwordError) {
            return;
        }

        setIsLoading(true)
        setSmartErrorKey('');
        const mfaUpdateCookieResponse = await api.mfaUpdateCookie({ password });
        setIsLoading(false);

        const redirectUrl = encodeURIComponent(`${window.location.origin}/account/login?error=SSO-1`);
        switch (mfaUpdateCookieResponse?.status) {
            case 200:
                onVerified(event);
                break;
            case 401:
                // password does not match, show inline
                setAttempts(prev => prev + 1);
                const localAttempts = attempts + 1;

                if (localAttempts === 4) {
                    setSmartErrorKey('TOO_MANY_ATTEMPTS');
                }

                if (localAttempts === 5) {
                    redirectTo(`/sso/signout?redirect_uri=${redirectUrl}`, 'Account is locked.');
                    return;
                }

                const inlineErrorText = `${translate("your_password_does_not_match")} ${translate('check_information_try_again')}`;
                setPasswordError(inlineErrorText);
                break;
            case 403:
                redirectTo(`/sso/signout?redirect_uri=${redirectUrl}`, 'Account is locked.');
                return;
            default:
                // unknown API error, show alert box
                setSmartErrorKey('UNKNOWN');
        }
    }

    function getTitleText() {
        if (flow === 'add' || flow === 'enroll') {
            return translate(`mfa_title_${flow}`);
        }
        if (type) {
            return translate(`mfa_title_edit_${type}`);
        }
    }

    function getMethodTypeText() {
        if (flow === 'add' || flow === 'enroll') {
            return translate(`verify_${flow}`);
        }
        if (type && flow === 'edit') {
            return translate(`verify_edit_${type}`);
        }
    }

    function updatePassword(passwordValue: string) {
        setShouldValidate(true);
        setPassword(passwordValue);
        setPasswordError('');
    }

    return (
        <Div
            isContainer={true}
            paddingySM={1} paddingyMD={2} paddingyLG={3} paddingyXL={4}
        >
            <form onSubmit={onSubmit} method="POST">
                <Div isRow>
                    <Div cols={12}>
                        <H1>
                            {getTitleText()}
                        </H1>
                    </Div>
                </Div>
                <SmartErrorAlert
                    name={'MfaVerifyPassword'}
                    passwordRef={null}
                    apiResponse={{}}
                    conversion={null}
                    invalidFields={null}
                    onInvalidField={() => {}}
                    errorKey={smartErrorKey}
                    parentHeading={translate(`mfa_title_${flow}`)}
                />
                <Div isRow>
                    <Div cols={12} colsMD={8} >
                        <p className="ds-u-margin-bottom--0">{getMethodTypeText()}</p>
                    </Div>
                </Div>
                <PasswordSection
                    errorMessage={shouldValidate && passwordError}
                    id={'current-password'}
                    label={translate('current_password')}
                    onChange={event => updatePassword(event.currentTarget.value)}
                    showForgotPasswordLink={false}
                    value={password}
                />

                <StackingButtons
                    button1={<SubmitButton isLoading={isLoading} mfaAnalyticsData={{
                        text: translate('verify_password'),
                        parentComponentHeading: getTitleText() || '',
                    }}>
                        {translate('verify_password')}
                    </SubmitButton>
                    }
                    button2={<CancelButton mfaAnalyticsHeading={getTitleText() || ''} onClick={() => history.goBack()} />}
                />
            </form>
        </Div>
    );
}